import React from 'react';
import { Risk, RiskStatus, RiskTolerability } from '../types';
import { TablePagination, useTablePagination } from '../../../../shared/TablePagination';
import { useTableSort, SortableTh } from '../../../../shared/useTableSort';

interface RisksTableProps {
  risks: Risk[];
  loading: boolean;
  onSelect: (risk: Risk) => void;
  onViewBowtie?: (risk: Risk) => void;
  onRefresh: () => void;
}

export const RisksTable: React.FC<RisksTableProps> = ({
  risks,
  loading,
  onSelect,
  onViewBowtie,
  onRefresh,
}) => {
  const {
    sortedItems,
    sortField,
    sortDirection,
    requestSort,
  } = useTableSort(risks);

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalItems,
    paginatedItems,
  } = useTablePagination(sortedItems, 25);
  const getStatusBadge = (status: RiskStatus) => {
    switch (status) {
      case 'DRAFT':
        return <span className="badge-pill badge-neutral">Draft</span>;
      case 'IDENTIFIED':
        return <span className="badge-pill badge-info">Identified</span>;
      case 'ASSESSED':
        return <span className="badge-pill badge-warning">Assessed</span>;
      case 'MITIGATION_PLANNED':
      case 'MITIGATION_IN_PROGRESS':
        return <span className="badge-pill badge-warning">{status.replace(/_/g, ' ')}</span>;
      case 'RESIDUAL_ACCEPTED':
      case 'MONITORED':
        return <span className="badge-pill badge-success">{status.replace(/_/g, ' ')}</span>;
      case 'CLOSED':
        return <span className="badge-pill badge-neutral">Closed</span>;
      default:
        return <span className="badge-pill badge-neutral">{String(status).replace(/_/g, ' ')}</span>;
    }
  };

  const getTolerabilityBadge = (index: string, tolerability?: RiskTolerability) => {
    let pillClass = 'badge-success';
    if (tolerability === 'INTOLERABLE') {
      pillClass = 'badge-danger';
    } else if (tolerability === 'TOLERABLE') {
      pillClass = 'badge-warning';
    }

    return (
      <span className={`badge-pill ${pillClass}`} title={`ICAO Index: ${index} (${tolerability || 'UNKNOWN'})`}>
        {index} · {tolerability?.slice(0, 3)}
      </span>
    );
  };

  const isOverdue = (nextReviewDate?: string, status?: RiskStatus) => {
    if (!nextReviewDate || status === 'CLOSED') return false;
    return new Date(nextReviewDate) < new Date();
  };

  if (!loading && risks.length === 0) {
    return (
      <div className="card" style={{ padding: '36px', textAlign: 'center', color: '#94a3b8' }}>
        No risk records found matching filter criteria.
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div className="table-container" style={{ margin: 0, border: 'none', borderRadius: 0 }}>
        <table className="data-table">
          <thead>
            <tr>
              <SortableTh field="riskNumber" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Risk Ref
              </SortableTh>
              <SortableTh field="title" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Risk Title & Context
              </SortableTh>
              <SortableTh field="status" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Status
              </SortableTh>
              <SortableTh field="initialRiskIndex" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Initial Risk
              </SortableTh>
              <SortableTh field="residualRiskIndex" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Residual Risk
              </SortableTh>
              <SortableTh field="riskOwnerName" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Owner / Dept
              </SortableTh>
              <SortableTh field="nextReviewDate" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Next Review
              </SortableTh>
              <th style={{ textAlign: 'right' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {paginatedItems.map((r) => {
              const overdue = isOverdue(r.nextReviewDate, r.status);
              return (
                <tr
                  key={r.id}
                  onClick={() => onSelect(r)}
                  style={{ cursor: 'pointer' }}
                >
                  <td style={{ fontWeight: 700, color: '#0284c7', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                    {r.riskNumber}
                  </td>
                  <td style={{ maxWidth: '340px' }}>
                    <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: '2px' }}>{r.title}</div>
                    {r.hazardNumber && (
                      <div style={{ fontSize: '11px', color: '#0369a1', fontWeight: 500 }}>
                        Linked Hazard: {r.hazardNumber} {r.hazardTitle ? `- ${r.hazardTitle}` : ''}
                      </div>
                    )}
                    {r.description && !r.hazardNumber && (
                      <div
                        style={{
                          fontSize: '12px',
                          color: '#64748b',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {r.description}
                      </div>
                    )}
                  </td>
                  <td>{getStatusBadge(r.status)}</td>
                  <td>
                    {getTolerabilityBadge(r.initialRiskIndex, r.initialTolerability)}
                  </td>
                  <td>
                    {r.residualRiskIndex ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ color: '#94a3b8', fontSize: '12px' }}>➔</span>
                        {getTolerabilityBadge(r.residualRiskIndex, r.residualTolerability)}
                      </div>
                    ) : (
                      <span style={{ fontSize: '11px', color: '#94a3b8', fontStyle: 'italic' }}>Pending Mitigation</span>
                    )}
                  </td>
                  <td style={{ fontSize: '12px', color: '#334155' }}>
                    <div style={{ fontWeight: 500 }}>{r.ownerName || 'Unassigned'}</div>
                    <div style={{ color: '#64748b', fontSize: '11px' }}>{r.department || 'General'}</div>
                  </td>
                  <td>
                    {r.nextReviewDate ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '12px', color: overdue ? '#dc2626' : '#475569', fontWeight: overdue ? 700 : 400 }}>
                          {new Date(r.nextReviewDate).toLocaleDateString()}
                        </span>
                        {overdue && (
                          <span className="badge-pill badge-danger" style={{ fontSize: '10px' }}>
                            OVERDUE
                          </span>
                        )}
                      </div>
                    ) : (
                      <span style={{ fontSize: '12px', color: '#94a3b8' }}>-</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                      {onViewBowtie && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewBowtie(r);
                          }}
                          className="btn-secondary"
                          style={{
                            width: 'auto',
                            padding: '5px 10px',
                            fontSize: '11px',
                            fontWeight: 600,
                            background: '#eff6ff',
                            color: '#2563eb',
                            border: '1px solid #bfdbfe',
                          }}
                          title="Open Interactive Bowtie Visualizer"
                        >
                          🎀 Bowtie
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelect(r);
                        }}
                        className="btn-secondary"
                        style={{ width: 'auto', padding: '5px 10px', fontSize: '12px' }}
                      >
                        Dossier ↗
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <TablePagination
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        itemLabel="risk assessments"
      />
    </div>
  );
};
